import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AllDeviceInfo, ROLES_KEYS, User } from '@interfaces';
import { allEmployeeRoles } from '@constants';
import { Observable, ReplaySubject } from 'rxjs';
import { UserAuthFacade } from '@state/user-auth';
import { DeviceInfoService } from '@core/services/device-info.service';
import { RouterFacade } from '@state/router';
import { environment } from '@environment';

@Component({
  selector: 'app-topbar-menu',
  templateUrl: './topbar-menu.component.html',
  styleUrls: ['./topbar-menu.component.scss'],
})
export class TopbarMenuComponent implements OnInit, OnDestroy {
  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @ViewChild('topbarMenuWindow') topbarMenuWindow: ElementRef<HTMLElement>;

  @Output() closeTopbarMenu: EventEmitter<void> = new EventEmitter<void>();

  public myself$: Observable<User | null> = this.userAuthFacade.myself$;

  public deviceInfo: AllDeviceInfo;
  public shouldShowOffice = false;
  public isAdministrator = false;

  public version = environment.version;

  private boundClickListener = this.clickListener.bind(this);

  constructor(
    private readonly userAuthFacade: UserAuthFacade,
    private readonly deviceInfoService: DeviceInfoService,
    private readonly routerFacade: RouterFacade
  ) {}

  public ngOnInit() {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
    });

    this.myself$
      .pipe(
        filter(user => !!user?.role),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(user => {
        const currentUserRoleKey = user.role.key as ROLES_KEYS;
        this.isAdministrator = currentUserRoleKey === ROLES_KEYS.Admin;

        this.shouldShowOffice = allEmployeeRoles.includes(user.role.key as ROLES_KEYS);
      });

    document.addEventListener('click', this.boundClickListener);
  }

  public ngOnDestroy(): void {
    document.removeEventListener('click', this.boundClickListener);
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public goTo(path: string): void {
    this.routerFacade.changeRoute({ linkParams: [path] });
  }

  public logout(): void {
    this.userAuthFacade.logout();
  }

  public closeProfileMenu(): void {
    this.closeTopbarMenu.emit();
  }

  private clickListener($event: MouseEvent): void {
    // if click has been inside window - do nothing
    if (this.topbarMenuWindow.nativeElement.contains($event.target as any)) {
      return;
    }

    const htmlTarget = $event.target as HTMLElement;
    if (
      htmlTarget.classList.contains('open-topbar-menu-button') ||
      htmlTarget.classList.contains('open-topbar-menu-icon')
    ) {
      return;
    }

    this.closeProfileMenu();
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: Event): void {
    const { key, code } = event as KeyboardEvent;
    if (key.toLowerCase() === 'escape' || code.toLowerCase() === 'escape') {
      this.closeProfileMenu();
    }
  }
}
